import { Box, BoxProps, Card, CardBody, CardFooter, CardHeader, Spinner, Text, TextProps } from 'grommet';
import { ColorType } from 'grommet/utils';
import { ReactNode } from 'react';
import { AddNewButton, BulkDeleteButton, SaveStatus, SaveStatusIndicator } from '..';

export interface ITalonCardProps extends React.PropsWithChildren {
  newItem?: () => void;
  disableAdd?: boolean;
  deleteItems?: () => Promise<void>;
  confirmDelete?: boolean;
  label: ReactNode | string;
  labelPlural?: string;
  buttons?: React.ReactNode[];
  selectedNames?: string;
  /** How many items are checked: the header's delete button shows once there's one */
  selectedCount?: number;
  headerChildren?: React.ReactNode;
  saveStatus?: SaveStatus;
  cardProps?: BoxProps;
  contentProps?: BoxProps;
  titleTextProps?: TextProps;
  margin?: BoxProps['margin'];
  headerHeight?: BoxProps['height'];
  headerBackground?: ColorType;
  bodyBackground?: BoxProps['background'];
  footerBackground?: BoxProps['background'];
  color?: TextProps['color'];
  hideFooter?: boolean;
  animate?: boolean;
  isLoading?: boolean;
  onHeaderClick?: () => void;
  headerCardProps?: BoxProps;
  /** The add button's label: "Add role" by default, from `label` */
  addButtonLabel?: string;
}

export const TalonCard = ({
  newItem,
  deleteItems,
  confirmDelete = true,
  disableAdd,
  label,
  labelPlural,
  buttons,
  children,
  headerChildren,
  selectedNames,
  selectedCount = 0,
  saveStatus,
  cardProps,
  contentProps,
  titleTextProps,
  margin = 'small',
  headerHeight,
  headerBackground,
  bodyBackground,
  footerBackground,
  color = 'text-heading',
  hideFooter = false,
  animate = false,
  isLoading = false,
  onHeaderClick,
  headerCardProps,
  addButtonLabel,
}: ITalonCardProps) => {
  /** Context **/

  /** State **/

  /** Computed **/
  const singular = typeof label === 'string' ? label.toLowerCase() : 'item';
  const plural = labelPlural?.toLowerCase() ?? `${singular}s`;

  /** Render **/
  return (
    <Card
      margin={margin}
      round="10px"
      border={{ color: 'border-card' }}
      elevation="none"
      background="white"
      {...cardProps}
    >
      {/* White, like the card: the brand gradient is kept for the app bar */}
      <CardHeader
        gap="small"
        background={headerBackground || 'white'}
        height={headerHeight || { min: '52px' }}
        pad={{ horizontal: '14px', vertical: 'xsmall' }}
        wrap
        border={{ side: 'bottom', color: 'border-divider' }}
        {...headerCardProps}
      >
        <Box
          direction="row"
          gap="small"
          flex="grow"
          align="center"
          style={{ zIndex: 2, cursor: onHeaderClick ? 'pointer' : undefined }}
          onClick={onHeaderClick}
        >
          <Text size="17px" weight={600} {...titleTextProps} color={color}>
            {labelPlural || label}
          </Text>
          <SaveStatusIndicator saveStatus={saveStatus} />
        </Box>
        {headerChildren}
        {/* Together and on the right, wrapping under the title when the card is narrow */}
        {((deleteItems && selectedCount > 0) || newItem) && (
          <Box direction="row" gap="small" align="center" margin={{ left: 'auto' }}>
            {deleteItems && (
              <BulkDeleteButton
                count={selectedCount}
                singular={singular}
                plural={plural}
                names={selectedNames}
                onDelete={deleteItems}
                confirm={confirmDelete}
              />
            )}
            {newItem && (
              <AddNewButton onClick={newItem} label={addButtonLabel ?? `Add ${singular}`} disabled={disableAdd} />
            )}
          </Box>
        )}
      </CardHeader>
      <CardBody
        flex="grow"
        align={isLoading ? 'center' : undefined}
        pad={isLoading ? 'medium' : undefined}
        background={bodyBackground || 'white'}
      >
        {isLoading && <Spinner />}
        <Box className={animate ? `animate-height ${isLoading ? 'collapsed' : 'expanded'}` : ''} {...contentProps}>
          {!isLoading && children}
        </Box>
      </CardBody>
      {!hideFooter && !!buttons?.length && (
        <CardFooter gap="xxsmall" background={footerBackground || 'light-1'} justify="end" pad="xsmall">
          {buttons}
        </CardFooter>
      )}
    </Card>
  );
};
